import React, { useState, useEffect } from 'react';
import { ArrowLeft, CreditCard, Loader2, LogOut } from 'lucide-react';
import { Button } from './ui/button';
import { membershipPlan } from '../mock';
import { subscriptionService } from '../services/subscription';
import { authService } from '../services/auth';
import { toast } from '../hooks/use-toast';
import { Toaster } from './ui/toaster';
import PaymentForm from './PaymentForm';
import { useNavigate } from 'react-router-dom';

const SubscriptionRequired = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [price, setPrice] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  useEffect(() => {
    // Verificar si ya tiene suscripción activa al cargar
    checkSubscriptionStatus();
    loadPrice();
  }, []);

  /**
   * Verificar si el usuario ya tiene suscripción activa
   * Si la tiene, redirigir automáticamente a /home
   */
  const checkSubscriptionStatus = async () => {
    try {
      setCheckingSubscription(true);
      
      // Verificar autenticación
      if (!authService.isAuthenticated()) {
        setCheckingSubscription(false);
        return;
      }

      // Obtener usuario actualizado del backend
      const user = await authService.getCurrentUserFromBackend();
      
      if (!user) {
        setCheckingSubscription(false);
        return;
      }

      // Admins no necesitan suscripción
      if (user.role === 'admin') {
        navigate('/admin');
        return;
      }

      // Verificar estado de suscripción
      try {
        const statusResponse = await subscriptionService.getStatus();
        
        const canAccessPremium = 
          statusResponse?.data?.can_access_premium ?? 
          statusResponse?.data?.has_active_subscription ?? 
          statusResponse?.data?.is_active ?? 
          false;

        if (canAccessPremium) {
          // Ya tiene suscripción activa - redirigir a home
          navigate('/home', { replace: true });
          return;
        }
      } catch (error) {
        // Si no tiene suscripción, es normal, continuar mostrando el formulario
        console.log('Usuario no tiene suscripción activa, mostrando formulario de pago');
      }
    } catch (error) {
      console.error('Error verificando suscripción:', error);
    } finally {
      setCheckingSubscription(false);
    }
  };

  /**
   * Refrescar estado del usuario y suscripción después del pago exitoso
   */
  const refreshUserAndSubscription = async () => {
    try {
      // 1. Refrescar datos del usuario desde el backend
      console.log('[SUBSCRIPTION] Refrescando datos del usuario...');
      const updatedUser = await authService.getCurrentUserFromBackend();
      
      if (updatedUser) {
        console.log('[SUBSCRIPTION] Usuario actualizado:', {
          id: updatedUser.id,
          name: updatedUser.name,
          subscription_status: updatedUser.subscription_status,
        });
      }

      // 2. Verificar estado de suscripción
      console.log('[SUBSCRIPTION] Verificando estado de suscripción...');
      let attempts = 0;
      const maxAttempts = 5;
      let subscriptionActive = false;

      while (attempts < maxAttempts && !subscriptionActive) {
        attempts++;
        console.log(`[SUBSCRIPTION] Intento ${attempts}/${maxAttempts} de verificación...`);
        
        try {
          const statusResponse = await subscriptionService.getStatus();
          
          const canAccessPremium = 
            statusResponse?.data?.can_access_premium ?? 
            statusResponse?.data?.has_active_subscription ?? 
            statusResponse?.data?.is_active ?? 
            false;

          if (canAccessPremium) {
            subscriptionActive = true;
            console.log('[SUBSCRIPTION] ✅ Suscripción activa confirmada');
            break;
          } else {
            console.log(`[SUBSCRIPTION] ⏳ Suscripción aún no activa, esperando... (intento ${attempts}/${maxAttempts})`);
            // Esperar 1 segundo antes del siguiente intento
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        } catch (error) {
          console.warn(`[SUBSCRIPTION] Error en intento ${attempts}:`, error.message);
          // Esperar antes de reintentar
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      return subscriptionActive;
    } catch (error) {
      console.error('[SUBSCRIPTION] Error refrescando usuario y suscripción:', error);
      return false;
    }
  };

  const loadPrice = async () => {
    try {
      const response = await subscriptionService.getPrice();
      if (response.success && response.data) {
        setPrice(response.data);
      }
    } catch (error) {
      console.error('Error cargando precio:', error);
    }
  };

  const handlePayment = async (paymentMethod) => {
    setLoading(true);
    setError(null);

    try {
      // Paso 1: Obtener precio
      let priceId;
      if (price && price.price_id) {
        priceId = price.price_id;
      } else {
        const priceResponse = await subscriptionService.getPrice();
        if (priceResponse && priceResponse.success) {
          priceId = priceResponse.data.price_id;
        } else {
          throw new Error('Error al obtener el precio de suscripción');
        }
      }

      // Paso 2: Verificar PaymentMethod
      if (!paymentMethod || !paymentMethod.id || !paymentMethod.id.startsWith('pm_')) {
        throw new Error('El método de pago no es válido. Intenta nuevamente.');
      }

      console.log('💳 Creando suscripción...');
      
      // Pequeño delay para asegurar que Stripe haya procesado el PaymentMethod
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Paso 3: Crear suscripción
      console.log('[DEBUG] ========================================');
      console.log('[DEBUG] Llamando a POST /api/subscription/create');
      console.log('[DEBUG] payment_method_id:', paymentMethod.id);
      console.log('[DEBUG] price_id:', priceId);
      console.log('[DEBUG] ========================================');
      
      const subscriptionResponse = await subscriptionService.createSubscription({
        payment_method_id: paymentMethod.id,
        price_id: priceId,
      });

      console.log('[DEBUG] ========================================');
      console.log('[DEBUG] Respuesta completa de /api/subscription/create');
      console.log('[DEBUG] success:', subscriptionResponse?.success);
      console.log('[DEBUG] error:', subscriptionResponse?.error);
      console.log('[DEBUG] message:', subscriptionResponse?.message);
      console.log('[DEBUG] ========================================');

      if (!subscriptionResponse || !subscriptionResponse.success) {
        console.error('[DEBUG] ❌ ERROR: La respuesta del backend indica fallo');
        console.error('[DEBUG] Error completo:', subscriptionResponse);
        throw new Error(subscriptionResponse?.error || 'Error al crear suscripción');
      }

      const subscriptionData = subscriptionResponse.data;
      
      // [SUBSCRIPTION] Logs detallados de la respuesta del backend
      console.log('[SUBSCRIPTION] ========================================');
      console.log('[SUBSCRIPTION] Respuesta /subscription/create:', subscriptionData);
      console.log('[SUBSCRIPTION] stripe_subscription_id:', subscriptionData.stripe_subscription_id || subscriptionData.subscription_id || 'NO ENCONTRADO');
      console.log('[SUBSCRIPTION] subscription_status:', subscriptionData.subscription_status || subscriptionData.status || 'NO ENCONTRADO');
      console.log('[SUBSCRIPTION] client_secret:', subscriptionData.client_secret ? `Sí (${subscriptionData.client_secret.substring(0, 30)}...)` : 'NO ENCONTRADO');
      console.log('[SUBSCRIPTION] client_secret_source:', subscriptionData.client_secret_source || 'NO ENCONTRADO');
      console.log('[SUBSCRIPTION] requires_payment_confirmation:', subscriptionData.requires_payment_confirmation ?? false);
      console.log('[SUBSCRIPTION] payment_intent_status:', subscriptionData.payment_intent_status || 'NO ENCONTRADO');
      console.log('[SUBSCRIPTION] ========================================');

      // Extraer datos de la respuesta
      const {
        stripe_subscription_id,
        subscription_status,
        client_secret,
        requires_payment_confirmation,
        payment_intent_status,
      } = subscriptionData;

      // Paso 4: Confirmar el pago desde el FRONTEND usando stripe.confirmCardPayment()
      // ESTRATEGIA: Usar requires_payment_confirmation del backend para decidir si confirmar
      
      let paymentConfirmed = false;

      // [SUBSCRIPTION] Evaluar si necesitamos confirmar el pago
      console.log('[SUBSCRIPTION] Evaluando si se debe confirmar el pago:', {
        has_client_secret: !!client_secret,
        requires_payment_confirmation,
        payment_intent_status,
      });

      // Si requires_payment_confirmation es true y hay client_secret, confirmar el pago
      if (requires_payment_confirmation === true && client_secret) {
        
        console.log('[SUBSCRIPTION] ========================================');
        console.log('[SUBSCRIPTION] confirmCardPayment llamado');
        console.log('[SUBSCRIPTION] client_secret:', client_secret.substring(0, 30) + '...');
        console.log('[SUBSCRIPTION] payment_method_id:', paymentMethod.id);
        console.log('[SUBSCRIPTION] ========================================');
        
        const { loadStripe } = await import('@stripe/stripe-js');
        const { STRIPE_PUBLIC_KEY } = await import('../config');
        const stripeInstance = await loadStripe(STRIPE_PUBLIC_KEY);
        
        if (!stripeInstance) {
          console.error('[SUBSCRIPTION] ❌ ERROR: No se pudo cargar Stripe');
          throw new Error('No se pudo cargar Stripe. Por favor, recarga la página.');
        }

        try {
          console.log('[SUBSCRIPTION] Ejecutando stripe.confirmCardPayment()...');
          const { error: confirmError, paymentIntent } = await stripeInstance.confirmCardPayment(
            client_secret,
            {
              payment_method: paymentMethod.id,
            }
          );

          console.log('[SUBSCRIPTION] ========================================');
          console.log('[SUBSCRIPTION] Resultado confirmCardPayment');
          console.log('[SUBSCRIPTION] confirmError:', confirmError ? {
            type: confirmError.type,
            code: confirmError.code,
            message: confirmError.message,
            decline_code: confirmError.decline_code,
          } : 'null');
          console.log('[SUBSCRIPTION] paymentIntent:', paymentIntent ? {
            id: paymentIntent.id,
            status: paymentIntent.status,
            client_secret: paymentIntent.client_secret ? paymentIntent.client_secret.substring(0, 20) + '...' : null,
          } : 'null');
          console.log('[SUBSCRIPTION] ========================================');

          if (confirmError) {
            console.error('[SUBSCRIPTION] ❌ ERROR confirmando pago:', confirmError);
            console.error('[SUBSCRIPTION] Error completo:', JSON.stringify(confirmError, null, 2));
            
            // Mostrar mensaje amigable al usuario
            const errorMessage = confirmError.message || 'Error al confirmar el pago. Por favor, intenta nuevamente.';
            toast({
              title: "Error al procesar el pago",
              description: errorMessage,
              variant: "destructive",
            });
            
            throw new Error(errorMessage);
          }

          if (!paymentIntent) {
            console.error('[SUBSCRIPTION] ❌ ERROR: No se recibió paymentIntent después de confirmar');
            throw new Error('No se recibió información del PaymentIntent después de confirmar.');
          }

          console.log('[SUBSCRIPTION] paymentIntent.status:', paymentIntent.status);

          // Manejar diferentes estados del PaymentIntent
          if (paymentIntent.status === 'succeeded') {
            paymentConfirmed = true;
            console.log('[SUBSCRIPTION] ✅ PaymentIntent succeeded - Pago confirmado exitosamente');
            
            // Mostrar mensaje de éxito
            toast({
              title: "¡Pago confirmado!",
              description: "Tu pago se ha procesado correctamente. Actualizando suscripción...",
            });
            
            // Esperar 2-3 segundos para que Stripe procese la suscripción
            console.log('[SUBSCRIPTION] Esperando que Stripe procese la suscripción (2 segundos)...');
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            // Actualizar suscripción en el backend llamando a /api/subscription/refresh
            try {
              console.log('[SUBSCRIPTION] Llamando a /api/subscription/refresh...');
              const refreshResponse = await subscriptionService.refreshSubscription();
              
              console.log('[SUBSCRIPTION] ========================================');
              console.log('[SUBSCRIPTION] Respuesta /subscription/refresh:', refreshResponse);
              if (refreshResponse && refreshResponse.success && refreshResponse.data) {
                const status = refreshResponse.data.status;
                console.log('[SUBSCRIPTION] subscription_status después de refresh:', status);
                console.log('[SUBSCRIPTION] payment_intent_status después de refresh:', refreshResponse.data.payment_intent_status);
                console.log('[SUBSCRIPTION] ========================================');
                
                if (status === 'active' || status === 'trialing') {
                  console.log('[SUBSCRIPTION] ✅ Suscripción activa confirmada');
                  paymentConfirmed = true;
                } else {
                  console.warn('[SUBSCRIPTION] ⚠️ Suscripción aún no está activa:', status);
                  // Intentar una vez más después de esperar
                  console.log('[SUBSCRIPTION] Esperando 2 segundos más y reintentando refresh...');
                  await new Promise(resolve => setTimeout(resolve, 2000));
                  const retryResponse = await subscriptionService.refreshSubscription();
                  if (retryResponse && retryResponse.success && retryResponse.data) {
                    console.log('[SUBSCRIPTION] subscription_status después de retry:', retryResponse.data.status);
                    if (retryResponse.data.status === 'active' || retryResponse.data.status === 'trialing') {
                      paymentConfirmed = true;
                    }
                  }
                }
              }
            } catch (refreshError) {
              console.error('[SUBSCRIPTION] ❌ Error al actualizar suscripción:', refreshError);
              // Continuar de todas formas - el webhook puede actualizar
            }
            
          } else if (paymentIntent.status === 'requires_action') {
            // Requiere 3D Secure - Stripe ya mostró el flujo y el usuario lo completó
            // El PaymentIntent puede estar en proceso, esperar y verificar
            console.log('[SUBSCRIPTION] ✅ PaymentIntent requires_action - 3D Secure completado, esperando procesamiento...');
            
            // Esperar para que Stripe procese después de 3D Secure
            await new Promise(resolve => setTimeout(resolve, 3000));
            
            // Verificar el estado actualizado llamando a refresh
            try {
              console.log('[SUBSCRIPTION] Llamando a /api/subscription/refresh después de 3D Secure...');
              const refreshResponse = await subscriptionService.refreshSubscription();
              if (refreshResponse && refreshResponse.success && refreshResponse.data) {
                const status = refreshResponse.data.status;
                const piStatus = refreshResponse.data.payment_intent_status;
                console.log('[SUBSCRIPTION] Estado después de 3D Secure:', { subscription_status: status, payment_intent_status: piStatus });
                
                if (status === 'active' || status === 'trialing' || piStatus === 'succeeded') {
                  paymentConfirmed = true;
                  console.log('[SUBSCRIPTION] ✅ Suscripción activada después de 3D Secure');
                } else {
                  // Esperar un poco más y verificar nuevamente
                  console.log('[SUBSCRIPTION] Esperando procesamiento adicional (2 segundos)...');
                  await new Promise(resolve => setTimeout(resolve, 2000));
                  const retryResponse = await subscriptionService.refreshSubscription();
                  if (retryResponse && retryResponse.success && retryResponse.data) {
                    console.log('[SUBSCRIPTION] Estado después de retry:', retryResponse.data);
                    if (retryResponse.data.status === 'active' || retryResponse.data.status === 'trialing') {
                      paymentConfirmed = true;
                    }
                  }
                }
              }
            } catch (refreshError) {
              console.warn('[SUBSCRIPTION] ⚠️ Error al actualizar suscripción después de 3D Secure:', refreshError.message);
            }
            
          } else {
            console.warn('[SUBSCRIPTION] ⚠️ PaymentIntent en estado inesperado:', paymentIntent.status);
            console.warn('[SUBSCRIPTION] Estados esperados: succeeded, requires_action');
            console.warn('[SUBSCRIPTION] Estado actual:', paymentIntent.status);
            
            // Esperar y verificar de todas formas
            await new Promise(resolve => setTimeout(resolve, 3000));
            try {
              console.log('[SUBSCRIPTION] Llamando a /api/subscription/refresh para verificar estado...');
              const refreshResponse = await subscriptionService.refreshSubscription();
              console.log('[SUBSCRIPTION] Estado después de espera:', refreshResponse?.data);
            } catch (refreshError) {
              console.warn('[SUBSCRIPTION] ⚠️ Error al actualizar suscripción:', refreshError.message);
            }
          }
          
        } catch (confirmErr) {
          console.error('[SUBSCRIPTION] ========================================');
          console.error('[SUBSCRIPTION] ❌ EXCEPCIÓN al confirmar pago');
          console.error('[SUBSCRIPTION] Error:', confirmErr.message);
          console.error('[SUBSCRIPTION] Stack:', confirmErr.stack);
          console.error('[SUBSCRIPTION] ========================================');
          throw confirmErr;
        }
        
      } else if (requires_payment_confirmation === false) {
        // Stripe no requiere confirmación manual (puede haber sido procesado automáticamente)
        console.log('[SUBSCRIPTION] Stripe no requiere confirmación manual');
        console.log('[SUBSCRIPTION] Llamando a /api/subscription/refresh para sincronizar estado...');
        
        // Aun así, llamar a refresh para sincronizar el estado
        try {
          await new Promise(resolve => setTimeout(resolve, 2000));
          const refreshResponse = await subscriptionService.refreshSubscription();
          console.log('[SUBSCRIPTION] Respuesta /subscription/refresh:', refreshResponse?.data);
          
          if (refreshResponse && refreshResponse.success && refreshResponse.data) {
            const status = refreshResponse.data.status;
            if (status === 'active' || status === 'trialing') {
              paymentConfirmed = true;
              console.log('[SUBSCRIPTION] ✅ Suscripción activa (sin confirmación manual requerida)');
            }
          }
        } catch (refreshError) {
          console.warn('[SUBSCRIPTION] ⚠️ Error al actualizar suscripción:', refreshError.message);
        }
        
      } else {
        // No hay client_secret o requires_payment_confirmation es false/null
        console.warn('[SUBSCRIPTION] ========================================');
        console.warn('[SUBSCRIPTION] ⚠️ NO se llamó a confirmCardPayment()');
        console.warn('[SUBSCRIPTION] Razón:');
        console.warn('[SUBSCRIPTION]   - has_client_secret:', !!client_secret);
        console.warn('[SUBSCRIPTION]   - requires_payment_confirmation:', requires_payment_confirmation);
        console.warn('[SUBSCRIPTION] ========================================');
        
        // Esperar y verificar el estado llamando a refresh
        await new Promise(resolve => setTimeout(resolve, 3000));
        try {
          console.log('[SUBSCRIPTION] Llamando a /api/subscription/refresh para verificar estado...');
          const refreshResponse = await subscriptionService.refreshSubscription();
          console.log('[SUBSCRIPTION] Respuesta /subscription/refresh:', refreshResponse?.data);
          
          if (refreshResponse && refreshResponse.success && refreshResponse.data) {
            const status = refreshResponse.data.status;
            console.log('[SUBSCRIPTION] Estado después de verificación:', status);
            if (status === 'active' || status === 'trialing') {
              paymentConfirmed = true;
              console.log('[SUBSCRIPTION] ✅ Suscripción activada automáticamente');
            } else {
              console.warn('[SUBSCRIPTION] ⚠️ Suscripción sigue en estado:', status);
            }
          }
        } catch (refreshError) {
          console.warn('[SUBSCRIPTION] ⚠️ Error al verificar suscripción:', refreshError.message);
        }
      }

      // Paso 5: Refrescar estado del usuario y suscripción
      console.log('[SUBSCRIPTION] ========================================');
      console.log('[SUBSCRIPTION] Iniciando refresh de usuario y suscripción...');
      console.log('[SUBSCRIPTION] ========================================');
      
      const subscriptionActive = await refreshUserAndSubscription();

      // Mostrar mensaje final y redirigir
      if (subscriptionActive || paymentConfirmed) {
        toast({
          title: "¡Suscripción activa!",
          description: "Tu suscripción está activa, disfruta tus beneficios 😉",
        });

        // Redirigir automáticamente a /home después de un breve delay
        console.log('[SUBSCRIPTION] Redirigiendo a /home...');
        setTimeout(() => {
          navigate('/home', { replace: true });
        }, 1500);
      } else {
        console.warn('[SUBSCRIPTION] ⚠️ El pago puede estar pendiente. La suscripción se activará cuando se procese.');
        toast({
          title: "Pago en proceso",
          description: "Tu pago está siendo procesado. La suscripción se activará en breve.",
          variant: "default",
        });
        
        // Aun así, intentar redirigir después de unos segundos
        // El RequireSubscription guard verificará y redirigirá si es necesario
        setTimeout(() => {
          navigate('/home', { replace: true });
        }, 3000);
      }

    } catch (err) {
      console.error('❌ Error en pago:', err);
      
      let errorMessage = 'Error al procesar el pago. Intenta nuevamente.';
      if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  // Mostrar loading mientras se verifica la suscripción inicial
  if (checkingSubscription) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-lg">Verificando suscripción...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black px-6 py-8">
      <div className="max-w-md mx-auto">
        <Toaster />
        
        {/* Header con botón de cerrar sesión */}
        <div className="flex items-center justify-between mb-8">
          <button 
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white transition-all hover:scale-105 active:scale-95"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm font-medium">Cerrar sesión</span>
          </button>
        </div>

        {/* Plan Info */}
        <div className="space-y-8 mb-8">
          <div>
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-white/10 mb-4 mx-auto">
              <CreditCard className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-2 text-center">
              Activa tu suscripción
            </h1>
            <p className="text-[#A2A2A2] text-center">
              Para acceder a todos los beneficios, necesitas una suscripción activa
            </p>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-3xl p-8 space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">{membershipPlan.name}</h2>
              <p className="text-4xl font-bold text-white">
                ${membershipPlan.price} 
                {/* <span className="text-xs text-[#A2A2A2] font-normal">{membershipPlan.currency} / {membershipPlan.period}</span> */}
{/* <span className="text-xs text-gray-600 font-normal align-baseline scale-75 inline-block">
  {membershipPlan.currency} / {membershipPlan.period}
</span> */}

              </p>
            </div>

            <div className="space-y-3">
              {membershipPlan.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center mt-0.5">
                    <div className="w-2 h-2 rounded-full bg-white"></div>
                  </div>
                  <p className="text-white">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Loading Overlay */}
        {loading && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="bg-white/10 border border-white/20 rounded-3xl p-8 max-w-sm mx-4 text-center">
              <div className="flex flex-col items-center space-y-4">
                <div className="relative">
                  <Loader2 className="w-16 h-16 text-white animate-spin" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-bold text-white">Creando tu suscripción</h3>
                  <p className="text-[#A2A2A2] text-sm">
                    Por favor espera mientras procesamos tu pago...
                  </p>
                </div>
                <div className="flex space-x-2 mt-4">
                  <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payment Form */}
        <div className="space-y-6">
          <h3 className="text-xl font-bold text-white">Pago seguro</h3>
          <PaymentForm
            onSubmit={handlePayment}
            loading={loading}
            error={error}
            amount={membershipPlan.price}
            currency={membershipPlan.currency}
          />
        </div>

        {/* Security Notice */}
        <div className="mt-8 text-center">
          <p className="text-[#A2A2A2] text-sm">
            🔒 Tu información está protegida por Stripe
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionRequired;

/*
 * ============================================================================
 * CAMBIOS REALIZADOS EN SubscriptionRequired.jsx
 * ============================================================================
 * 
 * FECHA: 2025-12-01
 * OBJETIVO: Ajustar el flujo de confirmación de pago para usar correctamente
 *           el client_secret que viene del backend (de confirmation_secret)
 * 
 * CAMBIOS PRINCIPALES:
 * 
 * 1. LECTURA DE RESPUESTA DEL BACKEND (línea ~86-97)
 *    - Ahora se extraen correctamente los campos de la respuesta:
 *      * stripe_subscription_id
 *      * subscription_status
 *      * client_secret
 *      * requires_payment_confirmation
 *      * payment_intent_status
 *    - Logs con prefijo [SUBSCRIPTION] para fácil identificación
 * 
 * 2. EVALUACIÓN DE CONFIRMACIÓN (línea ~99-115)
 *    - Cambiado de verificar payment_intent_status en estados específicos
 *    - A usar requires_payment_confirmation del backend
 *    - Si requires_payment_confirmation === true Y hay client_secret → confirmar
 * 
 * 3. LLAMADA A confirmCardPayment() (línea ~116-183)
 *    - Se llama cuando requires_payment_confirmation === true y client_secret existe
 *    - Logs claros antes y después de la llamada
 *    - Manejo de errores con mensajes amigables al usuario
 * 
 * 4. MANEJO DE RESULTADOS (línea ~184-264)
 *    - Si paymentIntent.status === 'succeeded':
 *      * Muestra mensaje de éxito
 *      * Espera 2 segundos
 *      * Llama a /api/subscription/refresh
 *      * Verifica que la suscripción esté 'active' o 'trialing'
 *      * Si no está activa, espera 2 segundos más y reintenta
 * 
 *    - Si paymentIntent.status === 'requires_action':
 *      * Espera 3 segundos (3D Secure completado)
 *      * Llama a /api/subscription/refresh
 *      * Verifica estado y reintenta si es necesario
 * 
 * 5. CASO SIN CONFIRMACIÓN REQUERIDA (línea ~275-295)
 *    - Si requires_payment_confirmation === false:
 *      * Solo loguea que no requiere confirmación
 *      * Aun así llama a /api/subscription/refresh para sincronizar
 * 
 * 6. LOGS MEJORADOS
 *    - Todos los logs usan prefijo [SUBSCRIPTION] para fácil búsqueda
 *    - Logs de respuesta de /subscription/create
 *    - Logs de resultado de confirmCardPayment
 *    - Logs de respuesta de /subscription/refresh
 * 
 * ============================================================================
 * DÓNDE VER LOS LOGS EN LA CONSOLA DEL NAVEGADOR
 * ============================================================================
 * 
 * 1. RESPUESTA DE /subscription/create:
 *    Buscar: [SUBSCRIPTION] Respuesta /subscription/create
 *    Verás:
 *    - stripe_subscription_id
 *    - subscription_status
 *    - client_secret (primeros 30 caracteres)
 *    - client_secret_source (confirmation_secret o payment_intent)
 *    - requires_payment_confirmation (true/false)
 *    - payment_intent_status
 * 
 * 2. LLAMADA A confirmCardPayment:
 *    Buscar: [SUBSCRIPTION] confirmCardPayment llamado
 *    Verás:
 *    - client_secret (primeros 30 caracteres)
 *    - payment_method_id
 * 
 * 3. RESULTADO DE confirmCardPayment:
 *    Buscar: [SUBSCRIPTION] Resultado confirmCardPayment
 *    Verás:
 *    - confirmError (si hay error, con type, code, message)
 *    - paymentIntent (id, status, client_secret)
 *    - paymentIntent.status (succeeded, requires_action, etc.)
 * 
 * 4. LLAMADA A /subscription/refresh:
 *    Buscar: [SUBSCRIPTION] Llamando a /api/subscription/refresh
 *    Verás:
 *    - Respuesta completa del refresh
 *    - subscription_status después de refresh
 *    - payment_intent_status después de refresh
 * 
 * 5. RESPUESTA DE /subscription/refresh:
 *    Buscar: [SUBSCRIPTION] Respuesta /subscription/refresh
 *    Verás:
 *    - Estado completo de la suscripción actualizada
 *    - subscription_status (debe ser 'active' o 'trialing')
 * 
 * ============================================================================
 * FLUJO ESPERADO DESPUÉS DE LOS CAMBIOS
 * ============================================================================
 * 
 * 1. Usuario ingresa tarjeta → Se crea PaymentMethod
 * 2. Se llama a POST /api/subscription/create
 * 3. Backend retorna:
 *    - client_secret (de confirmation_secret)
 *    - requires_payment_confirmation: true
 * 4. Frontend llama a stripe.confirmCardPayment(client_secret, ...)
 * 5. Si requiere 3D Secure → Stripe muestra modal, usuario completa
 * 6. PaymentIntent pasa a 'succeeded'
 * 7. Frontend espera 2 segundos
 * 8. Frontend llama a POST /api/subscription/refresh
 * 9. Backend actualiza suscripción a 'active' en BD
 * 10. Webhook invoice.payment_succeeded se dispara (respaldo)
 * 11. Suscripción queda en 'active' tanto en Stripe como en BD
 * 
 * ============================================================================
 */

